/**
 * §8.8 Release Notes — vertical timeline of platform releases.
 */

import React from 'react'
import clsx from 'clsx'
import { GitCommit, Sparkles, ArrowRight, Bug, Wrench, Zap } from 'lucide-react'
import { Container, Eyebrow, SectionHeading, GhostButton } from '@/components/landing/design-system'
import { RELEASES } from '../data'
import { TONE } from '../_shared'

export default function ReleaseNotes() {
  return (
    <section id="releases" className="relative py-24 md:py-28 scroll-mt-24">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Release Notes</Eyebrow>
          <SectionHeading align="center"
            title={<>Every improvement,{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">shipped in the open.</span></>}
            subtitle="A running timeline of new features, improvements and fixes. Every enhancement rolls out to every tenant automatically."
          />
        </div>

        <div className="relative">
          <div aria-hidden className="pointer-events-none absolute left-4 md:left-5 top-3 bottom-3 w-px bg-gradient-to-b from-brand-gold/50 via-white/10 to-transparent" />

          <div className="flex flex-col gap-4">
            {RELEASES.map((r) => {
              const tone = TONE[r.tone] || TONE.orange
              return (
                <div key={r.id} className="relative pl-12 md:pl-14">
                  {/* dot */}
                  <span className="absolute left-2 md:left-3 top-4 inline-flex h-5 w-5 items-center justify-center rounded-full border-2 border-black"
                    style={{ background: tone.hex, boxShadow: `0 0 0 3px rgba(${tone.rgb},0.25)` }}>
                    <GitCommit className="h-2.5 w-2.5 text-black" strokeWidth={3} />
                  </span>

                  <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-4 md:p-5 overflow-hidden relative">
                    <div className="pointer-events-none absolute inset-0 opacity-30"
                      style={{ background: `radial-gradient(60% 40% at 0% 0%, rgba(${tone.rgb},0.10), transparent 70%)` }} />
                    <div className="relative flex flex-wrap items-baseline gap-2 mb-3">
                      <span className="text-lg font-semibold text-white" style={{ color: tone.hex }}>{r.version}</span>
                      <span className="text-[11.5px] font-mono text-zinc-500">· {r.date}</span>
                      {r.tag && (
                        <span className="ml-auto inline-flex items-center gap-1 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2 py-0.5 text-[9.5px] font-mono uppercase tracking-wider text-emerald-400">
                          <Sparkles className="h-3 w-3" /> {r.tag}
                        </span>
                      )}
                    </div>

                    <div className="relative grid grid-cols-1 md:grid-cols-3 gap-3">
                      <NoteBlock icon={Zap}    tone="orange" label="New"          items={r.features} />
                      <NoteBlock icon={Wrench} tone="blue"   label="Improvements" items={r.improvements} />
                      <NoteBlock icon={Bug}    tone="green"  label="Fixes"        items={r.fixes} />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="flex items-center justify-center">
          <GhostButton href="#releases" trailingIcon>
            View full changelog <ArrowRight className="h-3 w-3" />
          </GhostButton>
        </div>
      </Container>
    </section>
  )
}

function NoteBlock({ icon: Icon, tone, label, items }) {
  const t = TONE[tone] || TONE.orange
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-md border"
          style={{ borderColor: `rgba(${t.rgb},0.35)`, background: `rgba(${t.rgb},0.08)`, color: t.hex }}>
          <Icon strokeWidth={1.8} style={{ width: 12, height: 12 }} />
        </span>
        <span className="text-[10.5px] font-mono uppercase tracking-wider" style={{ color: t.hex }}>{label}</span>
      </div>
      <ul className="flex flex-col gap-1">
        {items.map((it) => (
          <li key={it} className="text-[12px] text-zinc-300 leading-relaxed flex items-start gap-1.5">
            <span className="mt-1.5 h-1 w-1 rounded-full bg-zinc-500 shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
